import React from "react";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  LoaderCircle,
  Square,
  X,
} from "lucide-react";
import { canRequestOptimizationCancellation } from "../hooks/useOptimizationJobTracker.js";
import {
  buildOptimizationStatusNotice,
  formatOptimizationDispatchLabel,
  formatOptimizationStatusTime,
} from "../utils/appShellStatus.js";
import {
  buildOptimizationProgressSummary,
  buildOptimizationStopGuidance,
  formatOptimizationProgressDuration,
} from "../utils/optimizationProgress.js";
import { resolveOptimizationPreviewActivityLabel } from "../utils/optimizationPreviewMotion.js";
import { joinClasses } from "./ResultsPrimitives.jsx";

const TERMINAL_CARD_COLLAPSE_DELAY_MS = 8000;
const OPTIMIZATION_STAGE_LABELS = {
  assignments: "Calcul des affectations",
  dispatch_improvement: "Amélioration du Dispatch",
  routes: "Calcul des routes",
  final_verification: "Vérification finale",
  result_storage: "Enregistrement du résultat",
};
const OPTIMIZATION_COMPLETION_REASON_LABELS = {
  completed: "Optimisation terminée normalement.",
  stopped_without_checkpoint: "Arrêt confirmé sans nouveau résultat; le dispatch précédent a été conservé.",
};

function resolveOptimizationCount(...values) {
  for (const value of values) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue >= 0) {
      return Math.round(numericValue);
    }
  }
  return null;
}

export function resolveOptimizationActivityLabel(jobState, progress = null) {
  const assignmentPreview = jobState?.assignmentPreview && typeof jobState.assignmentPreview === "object"
    ? jobState.assignmentPreview
    : null;
  const checkpoint = jobState?.checkpoint && typeof jobState.checkpoint === "object"
    ? jobState.checkpoint
    : null;
  const activeStep = progress?.steps?.find((step) => step.key === progress.currentStepKey) || null;
  const activityCandidate = {
    ...(jobState && typeof jobState === "object" ? jobState : {}),
    ...(checkpoint || {}),
    ...(assignmentPreview || {}),
  };
  return resolveOptimizationPreviewActivityLabel(
    activityCandidate,
    {
      fallbackSource: assignmentPreview?.source || checkpoint?.source || jobState?.source,
      fallbackStage: activeStep?.key || checkpoint?.stageKey || checkpoint?.stage_key,
    },
  );
}

export function isFleetOptimizationProgressCardVisible(jobState, { isPreviewDrainActive = false } = {}) {
  const isTerminalPreviewDrain = isPreviewDrainActive === true
    && ["completed", "cancelled"].includes(jobState?.status);
  return jobState?.isDismissed !== true
    && (["running", "cancelling"].includes(jobState?.status) || isTerminalPreviewDrain);
}

export function OptimizationProgressTimeline({ jobStatus, progress }) {
  const activeStep = progress.steps.find((step) => step.key === progress.currentStepKey) || null;
  const shouldTick = activeStep?.status === "running" && ["running", "cancelling"].includes(jobStatus);
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!shouldTick) {
      return undefined;
    }

    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [progress.currentStepKey, shouldTick]);

  const summary = buildOptimizationProgressSummary(progress);
  const activeStatusLabel = activeStep?.status === "failed"
    ? "Échec"
    : activeStep?.status === "cancelled"
      ? "Interrompue"
      : jobStatus === "cancelling"
        ? "Arrêt demandé"
        : "En cours";

  return (
    <div className="grid gap-2.5 border-t border-black/8 pt-2.5" data-optimization-progress="true">
      <div className="flex flex-wrap items-center justify-between gap-1.5">
        <p className="m-0 text-[11px] font-bold text-slate-700">{summary}</p>
        <p className="m-0 text-[10px] text-slate-500">Progression cumulative</p>
      </div>

      <p className="sr-only" aria-live="polite">
        {activeStep ? `${summary}. ${activeStep.label} : ${activeStatusLabel}.` : summary}
      </p>

      <ol className="m-0 grid list-none gap-0 p-0" aria-label="Étapes de l'optimisation">
        {progress.steps.map((step, index) => {
          const isCurrentStep = step.key === progress.currentStepKey;
          const isCompleted = step.status === "completed";
          const isRunningStep = step.status === "running";
          const isFailed = step.status === "failed";
          const isCancelled = step.status === "cancelled";
          const startedAtMs = step.startedAt ? Date.parse(step.startedAt) : Number.NaN;
          const elapsedDurationMs = isRunningStep && Number.isFinite(startedAtMs)
            ? Math.max(nowMs - startedAtMs, 0)
            : step.durationMs;
          const durationLabel = formatOptimizationProgressDuration(elapsedDurationMs);
          const stepStatusLabel = isCompleted
            ? "Terminée"
            : isFailed
              ? "Échec"
              : isCancelled
                ? "Interrompue"
                : isRunningStep
                  ? (jobStatus === "cancelling" ? "Arrêt demandé" : "En cours")
                  : "À venir";

          return (
            <li
              key={step.key}
              className="relative grid min-w-0 grid-cols-[1.5rem_minmax(0,1fr)_auto] gap-x-2 pb-2.5 last:pb-0"
              aria-current={isCurrentStep && !isCompleted ? "step" : undefined}
              data-progress-step-status={step.status}
            >
              {index < progress.steps.length - 1 ? (
                <span
                  aria-hidden="true"
                  className={joinClasses([
                    "absolute left-[0.71875rem] top-6 h-[calc(100%-1rem)] w-px",
                    isCompleted ? "bg-[var(--color-accent)]" : "bg-slate-200",
                  ])}
                />
              ) : null}

              <span
                aria-hidden="true"
                className={joinClasses([
                  "relative z-1 inline-flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-extrabold shadow-xs",
                  isCompleted
                    ? "border-[var(--color-accent)] bg-[var(--color-accent)] text-white"
                    : isFailed
                      ? "border-red-500 bg-red-50 text-red-700"
                      : isCancelled
                        ? "border-amber-500 bg-amber-50 text-amber-700"
                        : isRunningStep
                          ? "border-accent bg-white text-accent"
                          : "border-slate-300 bg-slate-50 text-slate-500",
                ])}
              >
                {isCompleted ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                ) : isFailed || isCancelled ? (
                  <X className="h-3 w-3" strokeWidth={2.5} />
                ) : isRunningStep ? (
                  <LoaderCircle className="h-3.5 w-3.5 motion-safe:animate-spin motion-reduce:animate-none" />
                ) : (
                  index + 1
                )}
              </span>

              <div className="min-w-0">
                <p className={joinClasses([
                  "m-0 wrap-break-word text-[11px] font-semibold leading-4",
                  isRunningStep ? "text-slate-950" : isCompleted ? "text-slate-800" : "text-slate-600",
                ])}>
                  {step.label}
                </p>
                <p className={joinClasses([
                  "m-0 mt-px text-[10px] font-medium leading-3.5",
                  isCompleted
                    ? "text-[var(--color-accent)]"
                    : isFailed
                      ? "text-red-700"
                      : isCancelled || jobStatus === "cancelling" && isRunningStep
                        ? "text-amber-700"
                        : isRunningStep
                          ? "text-accent"
                          : "text-slate-400",
                ])}>
                  {stepStatusLabel}
                </p>
              </div>

              <span className="min-w-[2.75rem] pt-px text-right text-[10px] font-semibold tabular-nums text-slate-500">
                {durationLabel}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function OptimizationProgressCard({
  className = "",
  jobState,
  onCancel,
  onDismiss,
  optimizationPreviewReserve = null,
  optimizationPreviewQueueDepth = 0,
  optimizationPreviewRemainingMovementCount = null,
  isPreviewDrainActive = false,
  placement = "floating",
}) {
  const [cancelError, setCancelError] = React.useState("");
  const [isCancelPending, setIsCancelPending] = React.useState(false);
  const [isMinimized, setIsMinimized] = React.useState(false);
  const titleId = React.useId();

  React.useEffect(() => {
    setCancelError("");
    setIsCancelPending(false);
    setIsMinimized(false);

    if (isPreviewDrainActive) {
      return undefined;
    }

    if (!["completed", "cancelled"].includes(jobState?.status)) {
      return undefined;
    }

    const finishedAtMs = jobState?.finishedAt ? Date.parse(jobState.finishedAt) : Number.NaN;
    const elapsedMs = Number.isFinite(finishedAtMs) ? Math.max(Date.now() - finishedAtMs, 0) : 0;
    const remainingDelayMs = Math.max(TERMINAL_CARD_COLLAPSE_DELAY_MS - elapsedMs, 0);
    const timerId = window.setTimeout(() => {
      setIsMinimized(true);
    }, remainingDelayMs);
    return () => {
      window.clearTimeout(timerId);
    };
  }, [isPreviewDrainActive, jobState?.finishedAt, jobState?.jobId, jobState?.status]);

  if (!isFleetOptimizationProgressCardVisible(jobState, { isPreviewDrainActive })) {
    return null;
  }

  const notice = buildOptimizationStatusNotice(jobState);
  if (!notice) {
    return null;
  }

  const dispatchDateLabel = formatOptimizationDispatchLabel(
    notice.captureContext?.dispatch?.target_date,
    notice.captureContext?.dispatch?.target_period,
  );
  const dispatchStartedAtLabel = formatOptimizationStatusTime(notice.startedAt);
  const dispatchLabel = ["Dispatch", dispatchDateLabel, dispatchStartedAtLabel]
    .filter(Boolean)
    .join(" · ");
  const displayTitle = dispatchDateLabel && notice.title.endsWith(` — ${dispatchDateLabel}`)
    ? notice.title.slice(0, -(dispatchDateLabel.length + 3))
    : notice.title;

  const isRunning = notice.status === "running";
  const isCancelling = notice.status === "cancelling";
  const isCompleted = notice.status === "completed";
  const isCancelled = notice.status === "cancelled";
  const isPreviewMode = jobState?.previewMode !== "off"
    && (["running", "cancelling"].includes(notice.status) || isPreviewDrainActive);
  const rawCompletionReason = typeof jobState?.completionReason === "string"
    ? jobState.completionReason.trim()
    : "";
  const completionReason = OPTIMIZATION_COMPLETION_REASON_LABELS[rawCompletionReason]
    || rawCompletionReason;
  const assignmentPreview = jobState?.assignmentPreview && typeof jobState.assignmentPreview === "object"
    ? jobState.assignmentPreview
    : null;
  const checkpoint = jobState?.checkpoint && typeof jobState.checkpoint === "object"
    ? jobState.checkpoint
    : null;
  const previewAssignedOrderCount = resolveOptimizationCount(
    assignmentPreview?.assignedOrderCount,
    assignmentPreview?.assigned_order_count,
    checkpoint?.assignedOrderCount,
    checkpoint?.assigned_order_count,
    optimizationPreviewReserve?.revealedCount,
  );
  const previewTotalOrderCount = resolveOptimizationCount(
    assignmentPreview?.totalOrderCount,
    assignmentPreview?.total_order_count,
    checkpoint?.totalOrderCount,
    checkpoint?.total_order_count,
    optimizationPreviewReserve?.initialUnprotectedCount,
    optimizationPreviewReserve?.initialCount,
  );
  const previewRemainingMovementCount = resolveOptimizationCount(
    optimizationPreviewRemainingMovementCount,
    assignmentPreview?.remainingMovementCount,
    assignmentPreview?.remaining_movement_count,
    assignmentPreview?.remainingMovements,
    assignmentPreview?.remaining_movements,
    assignmentPreview?.movesRemaining,
    assignmentPreview?.moves_remaining,
    jobState?.remainingMovementCount,
    jobState?.remaining_movement_count,
    jobState?.remainingMovements,
    jobState?.remaining_movements,
    jobState?.movesRemaining,
    jobState?.moves_remaining,
  );
  const previewCountLabel = previewAssignedOrderCount !== null && previewTotalOrderCount !== null
    ? `${previewAssignedOrderCount}/${previewTotalOrderCount} positionnées`
    : "Aperçu provisoire";
  const reserveInitialCount = resolveOptimizationCount(
    optimizationPreviewReserve?.initialUnprotectedCount,
    optimizationPreviewReserve?.initialCount,
  );
  const reserveOrderIds = Array.isArray(optimizationPreviewReserve?.unprotectedOrderIds)
    ? optimizationPreviewReserve.unprotectedOrderIds.filter((orderId) => typeof orderId === "string" && orderId.trim())
    : [];
  const revealedOrderIds = Array.isArray(optimizationPreviewReserve?.revealedOrderIds)
    ? optimizationPreviewReserve.revealedOrderIds.filter((orderId) => typeof orderId === "string" && orderId.trim())
    : [];
  const revealedOrderIdSet = new Set(revealedOrderIds);
  const stationaryOrderIdCount = new Set(
    (Array.isArray(optimizationPreviewReserve?.stationaryOrderIds)
      ? optimizationPreviewReserve.stationaryOrderIds
      : [])
      .filter((orderId) => typeof orderId === "string" && orderId.trim())
      .map((orderId) => orderId.trim()),
  ).size;
  const reserveStationaryCount = resolveOptimizationCount(
    optimizationPreviewReserve?.stationaryCount,
    stationaryOrderIdCount,
  ) || 0;
  const reserveRevealedCount = reserveOrderIds.length
    ? reserveOrderIds.filter((orderId) => revealedOrderIdSet.has(orderId)).length
    : resolveOptimizationCount(optimizationPreviewReserve?.revealedCount) || 0;
  const reserveCount = reserveInitialCount === null
    ? null
    : Math.max(reserveInitialCount - reserveRevealedCount, 0);
  const canRequestCancellation = canRequestOptimizationCancellation(jobState);
  const stopGuidance = buildOptimizationStopGuidance({
    canRequestCancellation,
    cancellationAllowed: jobState?.cancellationAllowed,
    isRemote: jobState?.isRemote === true,
    jobStatus: notice.status,
    progress: notice.progress,
  });
  const isFinalizing = stopGuidance?.actionLabel === "Finalisation…";
  const statusSubline = notice.statusLabel;
  const Icon = isRunning || isCancelling ? LoaderCircle : isCompleted ? CheckCircle2 : AlertTriangle;
  const cardToneClassName = isCompleted
    ? "border-emerald-200 bg-[linear-gradient(180deg,rgba(240,253,250,0.99),rgba(255,255,255,0.98))]"
    : isCancelled || isCancelling
      ? "border-amber-200 bg-[linear-gradient(180deg,rgba(255,251,235,0.99),rgba(255,255,255,0.98))]"
      : notice.status === "failed"
        ? "border-red-200 bg-[linear-gradient(180deg,rgba(254,242,242,0.99),rgba(255,255,255,0.98))]"
        : "border-[rgba(15,92,87,0.2)] bg-[linear-gradient(180deg,rgba(240,253,250,0.99),rgba(255,255,255,0.98))]";
  const handleCancel = async () => {
    if (!stopGuidance?.canRequest || typeof onCancel !== "function" || isCancelPending) {
      return;
    }

    setCancelError("");
    setIsCancelPending(true);
    try {
      await onCancel();
      setIsCancelPending(false);
    } catch (error) {
      setCancelError(error instanceof Error && error.message
        ? error.message
        : "Impossible de demander l’arrêt de l’optimisation.");
      setIsCancelPending(false);
    }
  };

  if (isMinimized) {
    return (
      <button
        type="button"
        className={joinClasses([
          "pointer-events-auto flex w-full items-center gap-2 rounded-[8px] border px-3 py-2 text-left shadow-card-subtle transition hover:bg-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35",
          cardToneClassName,
          className,
        ])}
        data-fleet-optimization-progress-card={placement === "fleet" ? "true" : undefined}
        onClick={() => setIsMinimized(false)}
        aria-label="Afficher le suivi de l’optimisation"
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-[11px] font-bold text-slate-800">{notice.title}</span>
        <span className="text-[10px] font-semibold text-slate-500">Afficher</span>
      </button>
    );
  }

  return (
    <section
      className={joinClasses([
        "pointer-events-auto grid w-full min-w-0 gap-2.5 rounded-[8px] border p-3 shadow-card-subtle",
        cardToneClassName,
        className,
      ])}
      aria-busy={isRunning || isCancelling || isPreviewDrainActive}
      aria-labelledby={titleId}
      data-fleet-optimization-progress-card={placement === "fleet" ? "true" : undefined}
      data-optimization-progress-card="true"
      data-optimization-preview-mode={isPreviewMode ? "true" : undefined}
      data-optimization-preview-drain={isPreviewDrainActive ? "true" : undefined}
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-black/8 bg-white text-accent shadow-xs">
          <Icon className={joinClasses([
            "h-4 w-4",
            isRunning || isCancelling ? "motion-safe:animate-spin motion-reduce:animate-none" : "",
          ])} />
        </span>

        <div className="min-w-0 flex-1">
          {dispatchLabel ? (
            <p
              className="m-0 text-[11px] font-extrabold leading-4 text-[var(--color-accent)] wrap-break-word"
              data-optimization-dispatch-label="true"
            >
              {dispatchLabel}
            </p>
          ) : null}
          <h3 id={titleId} className="m-0 mt-0.5 text-[13px] font-bold leading-5 text-slate-900">
            {displayTitle}
          </h3>
          {!isRunning && statusSubline ? (
            <p className="m-0 mt-0.5 text-[10px] leading-4 text-slate-500" data-optimization-activity-label="true">
              {statusSubline}
            </p>
          ) : null}
        </div>

        {!isRunning && !isCancelling && !isPreviewDrainActive ? (
          <button
            type="button"
            onClick={onDismiss}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] border border-black/8 bg-white text-slate-500 transition hover:text-slate-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35"
            aria-label="Fermer le suivi de l’optimisation"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      {notice.progress ? (
        <OptimizationProgressTimeline jobStatus={notice.status} progress={notice.progress} />
      ) : (
        <p className="m-0 text-[11px] leading-4.5 text-slate-600">
          {notice.message || "Optimisation en cours."}
        </p>
      )}

      {isPreviewMode ? (
        <div
          className="grid gap-0.5 rounded-[7px] border border-[rgba(15,92,87,0.2)] bg-[rgba(240,249,246,0.82)] px-2.5 py-2"
          data-optimization-assignment-preview="true"
        >
          <p className="m-0 text-[10.5px] font-extrabold text-[var(--color-accent)]" data-optimization-preview-summary="true">
            {previewCountLabel}
          </p>
          <p
            className="m-0 text-[10px] leading-4 font-semibold text-amber-800"
            data-optimization-preview-warning="true"
          >
            Les affectations peuvent encore changer.
          </p>
          {previewRemainingMovementCount !== null ? (
            <p
              className="m-0 text-[10px] leading-4 font-semibold text-slate-600"
              data-optimization-preview-remaining-movements="true"
            >
              {previewRemainingMovementCount} mouvement{previewRemainingMovementCount === 1 ? "" : "s"} restant{previewRemainingMovementCount === 1 ? "" : "s"}
            </p>
          ) : Number(optimizationPreviewQueueDepth) > 0 ? (
            <p
              className="m-0 text-[10px] leading-4 font-semibold text-slate-600"
              data-optimization-preview-remaining-movements="true"
            >
              {Math.max(0, Math.round(Number(optimizationPreviewQueueDepth)))} mouvement{Number(optimizationPreviewQueueDepth) === 1 ? "" : "s"} restant{Number(optimizationPreviewQueueDepth) === 1 ? "" : "s"}
            </p>
          ) : null}
          {reserveCount !== null ? (
            <div
              className="mt-1 flex min-h-7 items-center justify-between gap-2 rounded-[6px] border border-[var(--color-accent)]/25 bg-white/85 px-2 py-1 text-[10px] font-extrabold text-[var(--color-accent)]"
              data-optimization-preview-staging="true"
            >
              <span>{reserveCount} commande{reserveCount === 1 ? "" : "s"} à redistribuer</span>
              <span
                className="flex flex-wrap justify-end gap-x-1 text-right text-[9px] font-semibold text-slate-500"
                data-optimization-preview-reserve-count="true"
              >
                <span>{reserveRevealedCount}/{reserveInitialCount} révélées</span>
                {reserveStationaryCount > 0 ? (
                  <span data-optimization-preview-stationary-count="true">
                    · {reserveStationaryCount} conservée{reserveStationaryCount === 1 ? "" : "s"} sur camion
                  </span>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {completionReason ? (
        <p className="m-0 text-[10px] leading-4 text-slate-600" data-optimization-completion-reason="true">
          {completionReason}
        </p>
      ) : null}

      {stopGuidance ? (
        <div className="grid gap-1.5 rounded-[7px] border border-amber-200/80 bg-amber-50/75 p-2.5">
          <div className="flex items-center justify-between gap-2">
            {stopGuidance.title !== "Arrêt possible maintenant" ? (
              <p className="m-0 text-[10.5px] font-extrabold text-amber-900">{stopGuidance.title}</p>
            ) : null}
            {stopGuidance.showAction || stopGuidance.canRequest || isCancelling ? (
              <button
                type="button"
                disabled={!stopGuidance.canRequest || isCancelPending}
                onClick={() => { void handleCancel(); }}
                className="inline-flex min-h-7 shrink-0 items-center gap-1.5 rounded-[6px] border border-amber-300 bg-white px-2.5 py-1 text-[10px] font-extrabold text-amber-900 transition hover:border-amber-400 hover:bg-amber-100 disabled:cursor-wait disabled:opacity-65 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-amber-400/40"
              >
                {isCancelPending || isCancelling || isFinalizing ? (
                  <LoaderCircle className="h-3 w-3 motion-safe:animate-spin motion-reduce:animate-none" />
                ) : (
                  <Square className="h-3 w-3" fill="currentColor" />
                )}
                <span>{isCancelPending ? "Demande…" : stopGuidance.actionLabel}</span>
              </button>
            ) : null}
          </div>
          <p className="m-0 text-[10px] leading-4 text-amber-900/85">{stopGuidance.message}</p>
        </div>
      ) : null}

      {cancelError ? (
        <p className="m-0 rounded-[6px] border border-red-200 bg-red-50 px-2.5 py-2 text-[10px] font-semibold leading-4 text-red-700" role="alert">
          {cancelError}
        </p>
      ) : null}

      {!isRunning && !isCancelling && notice.resultUrl ? (
        <a
          href={notice.resultUrl}
          className="justify-self-start text-[10.5px] font-bold text-accent underline-offset-2 hover:underline"
        >
          {notice.resultActionLabel} →
        </a>
      ) : null}
    </section>
  );
}
